import { FunctionComponent, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { faPause, faPlay } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { CampaignStatus } from "@/enum/campaign-status.enum";
import { UpdateType } from "@/enum/list.enum";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { useLazyGetListQuery, useUpdateListMutation } from "@/pages/Private/redux/list/list.api";
import { Spinner } from "@/components/Spinner/Spinner";

const LOADING_POLL_INTERVAL_MS = 5000;

export const PausedTag: FunctionComponent<{ row: ListPerson }> = ({ row }) => {
	const [isUpdating, setIsUpdating] = useState(false);
	const [updateList] = useUpdateListMutation();
	const [getList] = useLazyGetListQuery();
	const { t } = useTranslation();

	const isRunning = row.list.campaignStatus === CampaignStatus.RUNNING;
	const isLoading = row.list.campaignStatus === CampaignStatus.LOADING;

	const showsActiveStyle = isRunning || isLoading;

	const showSpinner = isUpdating || isLoading;

	const sawLoadingRef = useRef(false);

	useEffect(() => {
		if (isLoading) {
			sawLoadingRef.current = true;

			return;
		}

		if (sawLoadingRef.current && isUpdating) {
			sawLoadingRef.current = false;
			setIsUpdating(false);
		}
	}, [isLoading, isUpdating]);

	useEffect(() => {
		if (!isLoading) {
			return;
		}

		const listId = row.list.id;
		const interval = setInterval(() => {
			getList(listId, false);
		}, LOADING_POLL_INTERVAL_MS);

		return () => clearInterval(interval);
	}, [isLoading, row.list.id, getList]);

	// We no longer surface an "Outreach active" badge — when outreach is running there is nothing to
	// act on. Activation is prompted when assigning leads and surfaced as a dashboard issue instead.
	// The paused/loading states still render so the user can activate/resume from here.
	if (isRunning && !isLoading && !isUpdating) {
		return null;
	}

	const handleToggle = async (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (isUpdating || isLoading) {
			return;
		}

		sawLoadingRef.current = false;
		setIsUpdating(true);

		try {
			// arch-allow list-activation: this tag is labelled "Activate outreach for this list" — it is
			// the list-wide switch by name, not an assign that starts campaigns as a side effect.
			const result = await updateList({
				id: row.list.id,
				updateType: UpdateType.STATUS_WITH_REFETCH,
				campaignStatus: isRunning ? CampaignStatus.PAUSED : CampaignStatus.RUNNING,
			}).unwrap();

			if (result.campaignStatus !== CampaignStatus.LOADING) {
				setIsUpdating(false);
			}
		} catch (err) {
			console.error(err);
			setIsUpdating(false);
		}
	};

	return (
		<button
			disabled={showSpinner}
			className={`mr-1 mb-1 group ${
				showsActiveStyle
					? "bg-status-success-light/50 hover:bg-status-success  border-status-success/50"
					: "bg-status-warning-light/50 hover:bg-status-warning   border-status-warning/50"
			} px-2 whitespace-nowrap py-1 border border-border-light rounded-md text-sm text-left inline-flex flex-row gap-1 items-center cursor-pointer ${showSpinner ? "" : "transition-colors"} ${
				showSpinner ? "opacity-60" : ""
			}`}
			onClick={handleToggle}
		>
			<div className="relative">
				<span className="px-2 invisible whitespace-nowrap">
					{isRunning ? "Campaign Active" : "Activate outreach for this list"}
				</span>

				{showSpinner ? (
					<div className="absolute inset-0 flex items-center justify-center">
						<Spinner color={showsActiveStyle ? "#059669" : "#d97706"} />
						<div
							className={`ml-3 whitespace-nowrap ${showsActiveStyle ? "text-status-success hover:text-status-success-light" : "text-status-warning hover:text-status-warning-light"}`}
						>
							{isLoading ? t("basics.activatingCampaign") : t("basics.wait")}
						</div>
					</div>
				) : (
					<>
						<span
							className={`px-2 absolute inset-0 flex gap-1 items-center justify-center whitespace-nowrap opacity-100 group-hover:opacity-0 transition-opacity ${isRunning ? "text-status-success" : "text-status-warning"}`}
						>
							<span
								className={`inline-flex items-center justify-center rounded-full w-4 h-4 shrink-0 mr-1 ${isRunning ? "text-status-success bg-status-success/20" : "text-status-warning bg-status-warning/20"}`}
							>
								<Icon className="w-2.5 h-2.5" icon={isRunning ? faPlay : faPause} />
							</span>
							{isRunning ? "Outreach active" : "Outreach paused"}
						</span>

						<span
							className={`absolute inset-0 flex items-center justify-center whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity font-medium ${isRunning ? "text-status-success-light" : "text-status-warning-light"}`}
						>
							<span
								className={`inline-flex items-center justify-center rounded-full w-4 h-4 shrink-0 mr-1 ${isRunning ? "text-status-success bg-status-success-light" : "text-status-warning bg-status-warning-light"}`}
							>
								<Icon className="w-2.5 h-2.5" icon={isRunning ? faPause : faPlay} />
							</span>
							{isRunning ? "Pause outreach" : "Resume outreach"}
						</span>
					</>
				)}
			</div>
		</button>
	);
};
